import type { ExploreLevel, ExplorePlaceRef } from '@mollusk/shared';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useDeferredValue, useState } from 'react';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { api } from '../api/endpoints';
import { LoadingPill } from '../components/LoadingPill';
import { ExploreView } from '../features/explore/ExploreView';
import { searchString } from '../lib/searchParams';

const LEVELS: { value: ExploreLevel; label: string }[] = [
  { value: 'city', label: 'City' },
  { value: 'county', label: 'County' },
  { value: 'zip', label: 'ZIP' },
  { value: 'state', label: 'State' },
];

/** Explore (#172): pick a level and a place; see its permits and top tags. */
function ExplorePage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const level = search.level ?? 'city';
  const [input, setInput] = useState('');
  const q = useDeferredValue(input);
  const options = useQuery({
    queryKey: ['explore-places', level, q],
    queryFn: () => api.explorePlaces(level, q),
  });
  const place = useQuery({
    queryKey: ['explore-place', level, search.state, search.id],
    enabled: Boolean(search.state && search.id),
    queryFn: () => api.explorePlace({ level, state: search.state ?? '', id: search.id ?? '' }),
  });
  const pick = (p: ExplorePlaceRef) =>
    navigate({ to: '/explore', search: { level: p.level, state: p.state, id: p.id } });
  const notReady = options.error instanceof ApiError && options.error.code === 'NOT_READY';

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Explore
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={level}
          onChange={(_, v: ExploreLevel | null) => {
            if (v) {
              setInput('');
              navigate({ to: '/explore', search: { level: v } });
            }
          }}
          aria-label="Level"
          sx={{ bgcolor: 'background.paper' }}
        >
          {LEVELS.map((l) => (
            <ToggleButton key={l.value} value={l.value}>
              {l.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Autocomplete
          value={place.data ?? null}
          options={options.data?.places ?? []}
          getOptionLabel={(p) => p.name}
          filterOptions={(o) => o}
          isOptionEqualToValue={(a, b) => a.id === b.id && a.level === b.level}
          inputValue={input}
          onInputChange={(_, v) => setInput(v)}
          onChange={(_, v) => {
            if (v) pick(v);
          }}
          loading={options.isFetching}
          renderOption={(props, p) => (
            <li {...props} key={`${p.level}:${p.id}`}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                <span>{p.name}</span>
                <Typography variant="body2" color="text.secondary">
                  {p.n.toLocaleString()}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Pick a ${LEVELS.find((l) => l.value === level)?.label.toLowerCase()}`}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
            />
          )}
          sx={{ minWidth: 300, flex: '1 1 300px', maxWidth: 480 }}
        />
      </Box>
      {notReady ? (
        <Alert severity="info">Explore data is being prepared; try again shortly.</Alert>
      ) : null}
      {place.data ? (
        <ExploreView
          key={`${place.data.level}:${place.data.id}`}
          place={place.data}
          thisYear={new Date().getFullYear()}
        />
      ) : place.isFetching ? (
        <LoadingPill label="Loading…" />
      ) : !notReady ? (
        <Typography color="text.secondary">
          Pick a place to see its permits by year and its top tags.
        </Typography>
      ) : null}
    </Box>
  );
}

export const Route = createFileRoute('/explore')({
  validateSearch: z.object({
    level: z.enum(['city', 'county', 'zip', 'state']).optional(),
    state: searchString.optional(),
    id: searchString.optional(),
  }),
  component: ExplorePage,
});
